import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { unreadMessagesAPI, channelsAPI } from '../lib/api';
import { useAuth } from './AuthContext';

const UnreadMessagesContext = createContext(null);

export const useUnreadMessages = () => {
  const context = useContext(UnreadMessagesContext);
  if (!context) {
    throw new Error('useUnreadMessages must be used within an UnreadMessagesProvider');
  }
  return context;
};

export const UnreadMessagesProvider = ({ children }) => {
  const { user } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);
  const [unreadByConversation, setUnreadByConversation] = useState({});
  const [channelUnreadCount, setChannelUnreadCount] = useState(0);
  const [channelUnreadByChannel, setChannelUnreadByChannel] = useState({});
  const isMounted = useRef(true);

  const fetchUnreadCount = useCallback(async () => {
    if (!user) {
      setUnreadCount(0);
      setUnreadByConversation({});
      setChannelUnreadCount(0);
      setChannelUnreadByChannel({});
      return;
    }
    
    try {
      const [dmRes, chRes] = await Promise.all([
        unreadMessagesAPI.getCount(),
        channelsAPI.getUnreadCounts().catch(() => ({ data: { total_unread: 0, by_channel: {} } })),
      ]);
      if (isMounted.current) {
        setUnreadCount(dmRes.data.unread_count);
        setUnreadByConversation(dmRes.data.unread_by_conversation || {});
        setChannelUnreadCount(chRes.data.total_unread || 0);
        setChannelUnreadByChannel(chRes.data.by_channel || {});
      }
    } catch (error) {
      console.error('Failed to fetch unread messages count:', error);
    }
  }, [user]);

  const markConversationRead = useCallback(async (conversationId) => {
    try {
      await unreadMessagesAPI.markConversationRead(conversationId);
      setUnreadByConversation(prev => {
        const newByConv = { ...prev };
        const conversationUnread = newByConv[conversationId] || 0;
        delete newByConv[conversationId];
        setUnreadCount(c => Math.max(0, c - conversationUnread));
        return newByConv;
      });
    } catch (error) {
      console.error('Failed to mark conversation as read:', error);
    }
  }, []);

  const markChannelRead = useCallback(async (channelId) => {
    try {
      await channelsAPI.markRead(channelId);
      setChannelUnreadByChannel(prev => {
        const newMap = { ...prev };
        const cnt = newMap[channelId] || 0;
        delete newMap[channelId];
        setChannelUnreadCount(c => Math.max(0, c - cnt));
        return newMap;
      });
    } catch (error) {
      console.error('Failed to mark channel as read:', error);
    }
  }, []);

  const refreshUnreadCount = useCallback(() => {
    fetchUnreadCount();
  }, [fetchUnreadCount]);

  useEffect(() => {
    isMounted.current = true;
    const timeoutId = setTimeout(() => { fetchUnreadCount(); }, 0);
    const interval = setInterval(() => {
      const { isPollingPaused } = require('../lib/api');
      if (!isPollingPaused()) {
        fetchUnreadCount();
      }
    }, 30000);
    return () => {
      isMounted.current = false;
      clearTimeout(timeoutId);
      clearInterval(interval);
    };
  }, [fetchUnreadCount]);

  const totalUnread = unreadCount + channelUnreadCount;

  const value = {
    unreadCount: totalUnread,
    dmUnreadCount: unreadCount,
    channelUnreadCount,
    unreadByConversation,
    channelUnreadByChannel,
    markConversationRead,
    markChannelRead,
    refreshUnreadCount,
  };

  return (
    <UnreadMessagesContext.Provider value={value}>
      {children}
    </UnreadMessagesContext.Provider>
  );
};
